import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import * as attendanceService from "../../Services/attendanceService";
import * as siteService from "../../Services/siteServices";
import { Spinner } from "../../Components/Common";
import { MESSAGES } from "./constants";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";

const validationSchema = Yup.object({
  site_id: Yup.string().required(MESSAGES.SITES_NAME_REQUIRED),
  date: Yup.date()
    .max(new Date(Date.now() - 24 * 60 * 60 * 1000), MESSAGES.FUTURE_DATE_ERROR)
    .required(MESSAGES.DATE_REQUIRED),
  check_in_time: Yup.string().required(MESSAGES.TIME_REQUIRED),
  check_out_time: Yup.string()
    .required(MESSAGES.TIME_REQUIRED)
    .test(
      "check-out-after-check-in",
      MESSAGES.CHECKOUT_PRE_CHECKIN,
      function (value) {
        const { check_in_time } = this.parent;

        if (check_in_time && value) {
          const checkInTime = new Date(`1970-01-01T${check_in_time}:00`);
          const checkOutTime = new Date(`1970-01-01T${value}:00`);

          return checkOutTime > checkInTime;
        }
        return true;
      }
    ),
});

const Backdated = () => {
  const [siteOptions, setSiteOptions] = useState([]);
  const navigate = useNavigate();

  const initialAttendanceValue = {
    site_id: "",
    date: "",
    check_in_time: "",
    check_out_time: "",
  };

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append("site", values.site_id);
    formData.append("date", values.date);
    formData.append("is_backdated", 1);
    formData.append("check_in_time", values.check_in_time);
    formData.append("check_out_time", values.check_out_time);

    try {
      const response = await attendanceService.backDated(formData);
      if (response.status === 201) {
        toast.success(MESSAGES.ENTRY_SUBMITTED);
        navigate(-1);
      } else {
        const msg = extractErrorMessage(response) || MESSAGES.FAILED_SUBMISSION;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.FAILED_SUBMISSION;
      toast.error(msg);
    }
  };

  useEffect(() => {
    const fetchSites = async () => {
      try {
        // const response = await siteService.getSites(GET_ALL_PAGINATION);
        const response = await siteService.getSites();
        setSiteOptions(response.data.results);
      } catch (error) {
        toast.error(MESSAGES.SITES_ERROR);
      }
    };

    fetchSites();
  }, []);

  return (
    <div>
      <div className="max-w-3xl mx-auto p-4 rounded-lg flex justify-center items-center">
        <div className="bg-white rounded-lg p-4 w-full max-w-xl overflow-y-auto max-h-screen shadow-md">
          <div className="relative flex items-center justify-center mb-4 h-12">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="absolute left-0 text-primary hover:text-secondary text-2xl pl-2"
              title="Back"
            >
              <HiOutlineArrowLeft />
            </button>
            <h2 className="text-2xl font-bold text-center text-primary">
              Backdated Attendance
            </h2>
          </div>
          <Formik
            initialValues={initialAttendanceValue}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) =>
              isSubmitting ? (
                <div className="flex justify-center items-center h-40">
                  <Spinner />
                </div>
              ) : (
                <Form className="space-y-4 text-secondary">
                  {/* Site Dropdown */}
                  <div>
                    <label className="block font-medium">Site</label>
                    <Field
                      as="select"
                      name="site_id"
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="">Select a site</option>
                      {siteOptions.map((site) => (
                        <option key={site.id} value={site.id}>
                          {site.site_name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="site_id"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>

                  {/* Date Field */}
                  <div>
                    <label className="block font-medium">Date</label>
                    <Field
                      name="date"
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                      onInput={(e) => {
                        const value = e.target.value;
                        const year = value.split("-")[0];
                        if (year.length > 4) {
                          e.target.value = "";
                        }
                      }}
                    />
                    <ErrorMessage
                      name="date"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>

                  {/* Check-In Time Field */}
                  <div>
                    <label className="block font-medium">Check-In Time</label>
                    <Field
                      name="check_in_time"
                      type="time"
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    />
                    <ErrorMessage
                      name="check_in_time"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>

                  {/* Check-Out Time Field */}
                  <div>
                    <label className="block font-medium">Check-Out Time</label>
                    <Field
                      name="check_out_time"
                      type="time"
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    />
                    <ErrorMessage
                      name="check_out_time"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-between mt-8">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary text-white rounded hover:bg-secondary"
                    >
                      Submit
                    </button>
                  </div>
                </Form>
              )
            }
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default Backdated;